import type { Failure } from "@/modules/shared/failure";
import { renderFieldErrors } from "@/modules/shared/fieldCopy";
import { SYSTEM_ERROR, type FormState } from "@/modules/shared/forms";
import { NOT_CONFIGURED } from "./authCopy";

/**
 * The **web's** copy for the Consumer's own account screen (R22 T13).
 *
 * Lifted out of `./profile.ts` for the same reason as its siblings: that file
 * is `"use server"`, so these could be neither shared with the static web app
 * nor pinned by a test. Every sentence is unchanged.
 *
 * The two refusals render **under a field** rather than in a banner, and which
 * field is the point: one names the box they mistyped, the other names the box
 * they have to change. It is the same pair the staff password change has, and
 * deliberately not the same function: these two screens can diverge (this one
 * has no "we sent it to you" hint), and folding them together would mean the
 * next change to one silently editing the other.
 */
export function profileStateFor(failure: Failure): FormState {
  if (failure.fieldErrors) {
    return { fieldErrors: renderFieldErrors(failure.fieldErrors) };
  }
  switch (failure.code) {
    case "current_password_incorrect":
      return {
        fieldErrors: { currentPassword: "Current password is incorrect." },
      };
    case "password_unchanged":
      return {
        fieldErrors: {
          newPassword: "Choose a password different from your current one.",
        },
      };
    case "not_configured":
      return { formError: NOT_CONFIGURED };
    default:
      return { formError: SYSTEM_ERROR };
  }
}

/**
 * The two confirmations this screen shows.
 *
 * Sentences rather than tokens, because they are rendered straight into a
 * success banner. They are here so both frontends say the same thing after the
 * same act, which is exactly the kind of copy that drifts unnoticed: nobody
 * files a bug because one client says "saved" and the other says "updated".
 */
export const NAME_SAVED = "Your name has been updated.";
export const PASSWORD_CHANGED = "Your password has been changed.";

/**
 * A refused member deletion (H58), as the business settings render it.
 *
 * **The sole-Manager refusal names the clinic**, because a member of several
 * clinics has to know which one to hand over, and "make someone else a
 * manager" is only actionable with a place attached. The apps say the same
 * sentence (`MemberCopy.deleteRefusal`).
 */
export function memberDeletionStateFor(failure: Failure): FormState {
  if (failure.code === "last_active_manager") {
    const clinic = failure.businessName?.trim() || "your clinic";
    return {
      formError:
        `You are the only manager at ${clinic}. Make someone else a manager there first, ` +
        "or contact support to close the clinic.",
    };
  }
  return profileStateFor(failure);
}
